import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { motion, AnimatePresence } from 'framer-motion';
import GlassCard from './GlassCard';
import ProductModal from './ProductModal';

const SkeletonCard = () => (
  <div className="bg-gray-50 border border-gray-100 rounded-[2.5rem] p-4 animate-pulse">
    <div className="w-full aspect-square bg-gray-200 rounded-[2rem] mb-4" />
    <div className="h-4 bg-gray-200 rounded-full w-3/4 mb-2" />
    <div className="h-3 bg-gray-200 rounded-full w-1/2" />
  </div>
);

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCat, setSelectedCat] = useState('Todas');
  const [selectedSub, setSelectedSub] = useState('Todas');
  
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [whatsapp, setWhatsapp] = useState('');

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const configSnap = await getDoc(doc(db, "settings", "home"));
        if (configSnap.exists()) {
          const data = configSnap.data();
          setCategories(['Todas', ...(data.categories || [])]);
          setSubCategories(['Todas', ...(data.subCategories || [])]);
          setWhatsapp(data.whatsappNumber || '');
        }

        const prodSnap = await getDocs(query(collection(db, "products"), orderBy("name")));
        const allProducts = prodSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        const sorted = allProducts.sort((a, b) => {
          const aIsNew = a.categories?.includes("Novedad");
          const bIsNew = b.categories?.includes("Novedad");
          if (aIsNew && !bIsNew) return -1;
          if (!aIsNew && bIsNew) return 1;
          return 0;
        });

        setProducts(sorted);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    loadData();
  }, []);

  const openProduct = (p) => {
    setSelectedProduct(p);
    setIsModalOpen(true);
  };

  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase());
    const matchesCat = selectedCat === 'Todas' || p.categories?.includes(selectedCat);
    const matchesSub = selectedSub === 'Todas' || p.subCategories?.includes(selectedSub);
    return matchesSearch && matchesCat && matchesSub;
  });

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      className="bg-white pt-32 pb-20 px-4 md:px-6 max-w-7xl mx-auto min-h-screen selection:bg-[#a6192e] selection:text-white"
    >
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-8">
        <div className="space-y-2">
          <span className="text-[#a6192e] font-black text-[10px] tracking-[0.5em] uppercase italic">Nuestros productos</span>
          <h2 className="text-6xl md:text-8xl font-black tracking-tighter italic leading-none uppercase text-slate-900">
            CATÁLOGO
          </h2>
        </div>
        <div className="w-full md:w-96 relative">
          <input 
            type="text" 
            placeholder="Buscar por modelo o clave..." 
            className="w-full bg-gray-50 border border-gray-100 p-5 rounded-2xl outline-none focus:border-[#a6192e] focus:bg-white transition-all text-sm italic text-slate-700 shadow-inner"
            onChange={e => setSearch(e.target.value)}
          />
          <svg className="absolute right-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      <div className="mb-16 space-y-6">
        <div className="space-y-3">
          <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Categorías</p>
          <div className="flex gap-2 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden pb-2">
            {categories.map(cat => (
              <button 
                key={cat} 
                onClick={() => setSelectedCat(cat)} 
                className={`px-6 py-3 rounded-xl text-[10px] font-black transition-all border whitespace-nowrap uppercase italic ${
                  selectedCat === cat 
                  ? 'bg-[#a6192e] border-[#a6192e] text-white shadow-xl shadow-red-100' 
                  : 'border-gray-100 bg-gray-50 text-slate-400 hover:border-gray-300'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Subcategorías / Aplicaciones</p>
          <div className="flex gap-2 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden pb-2">
            {subCategories.map(sub => (
              <button 
                key={sub} 
                onClick={() => setSelectedSub(sub)} 
                className={`px-6 py-3 rounded-xl text-[10px] font-black transition-all border whitespace-nowrap uppercase italic ${
                  selectedSub === sub 
                  ? 'bg-slate-900 border-slate-900 text-white shadow-xl shadow-slate-200' 
                  : 'border-gray-100 bg-gray-50 text-slate-400 hover:border-gray-300'
                }`}
              >
                {sub}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {loading ? (
          Array(8).fill(0).map((_, i) => <SkeletonCard key={i} />)
        ) : (
          <AnimatePresence mode='popLayout'>
            {filteredProducts.length > 0 ? (
              filteredProducts.map((p) => (
                <motion.div
                  key={p.id}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="relative"
                >
                  {p.categories?.includes("Novedad") && (
                    <div className="absolute top-6 left-6 z-20 bg-[#a6192e] text-white text-[7px] font-black px-3 py-1 rounded-full uppercase tracking-widest shadow-xl shadow-red-200 italic">
                      Novedad
                    </div>
                  )}
                  <GlassCard item={p} onOpen={() => openProduct(p)} />
                </motion.div>
              ))
            ) : (
              <div className="col-span-full py-20 text-center">
                <p className="text-slate-300 font-black italic uppercase text-2xl tracking-tighter">No se encontraron insumos</p>
                <button onClick={() => {setSearch(''); setSelectedCat('Todas'); setSelectedSub('Todas');}} className="mt-4 text-[#a6192e] text-[10px] font-bold uppercase tracking-widest border-b border-[#a6192e]">Limpiar filtros</button>
              </div>
            )}
          </AnimatePresence>
        )}
      </div>

      <AnimatePresence>
        {isModalOpen && (
          <ProductModal 
            isOpen={isModalOpen} 
            onClose={() => setIsModalOpen(false)} 
            product={selectedProduct} 
            whatsappNumber={whatsapp} 
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ProductsPage;